<template>
  <div class="escolha-de-pais">
    <!-- O campo: a escolha atual, e o que abre a lista. Some quando quem usa controla a abertura. -->
    <button
      v-if="!soPainel"
      type="button"
      class="escolha-de-pais__campo"
      :aria-expanded="String(aberto)"
      :disabled="desabilitado"
      @click="alternar"
    >
      <BandeiraDePais v-if="modelValue" :codigo="modelValue" :escala="2" />
      <FishIcon v-else nome="globe" tamanho="pequeno" />
      <span class="escolha-de-pais__nome crescer">{{ nomeAtual || $t('clan.pais.nenhum') }}</span>
      <span class="escolha-de-pais__trocar">
        {{ aberto ? $t('clan.pais.fechar') : $t('clan.pais.trocar') }}
      </span>
    </button>

    <div v-if="aberto || soPainel" class="escolha-de-pais__painel">
      <input
        ref="busca"
        v-model="busca"
        type="search"
        class="escolha-de-pais__busca"
        :placeholder="$t('clan.pais.buscar')"
        :aria-label="$t('clan.pais.buscar')"
      />

      <ul ref="grade" class="escolha-de-pais__grade">
        <!-- "Sem país" primeiro e só sem busca: é uma escolha, não um país que se procura. -->
        <li v-if="!busca">
          <button
            type="button"
            class="escolha-de-pais__opcao"
            :class="{ 'escolha-de-pais__opcao--atual': !modelValue }"
            :aria-pressed="String(!modelValue)"
            :disabled="desabilitado"
            @click="escolher(null)"
          >
            <span class="escolha-de-pais__sem"><FishIcon nome="globe" tamanho="pequeno" /></span>
            <span class="escolha-de-pais__rotulo">{{ $t('clan.pais.nenhum') }}</span>
          </button>
        </li>
        <li v-for="pais in filtrados" :key="pais.codigo">
          <button
            type="button"
            class="escolha-de-pais__opcao"
            :class="{ 'escolha-de-pais__opcao--atual': pais.codigo === modelValue }"
            :aria-pressed="String(pais.codigo === modelValue)"
            :title="pais.nome"
            :disabled="desabilitado"
            @click="escolher(pais.codigo)"
          >
            <BandeiraDePais :codigo="pais.codigo" :escala="2" />
            <span class="escolha-de-pais__rotulo">{{ pais.nome }}</span>
          </button>
        </li>
      </ul>

      <p v-if="busca && !filtrados.length" class="escolha-de-pais__vazio">
        {{ $t('clan.pais.nadaEncontrado') }}
      </p>
    </div>
  </div>
</template>

<script>
import { FishIcon } from '@/shared/ui';
import BandeiraDePais from './BandeiraDePais.vue';
import { CODIGOS_DE_PAIS } from './paises.gerado';
import { nomeDoPais, semAcento } from './paises';

/**
 * O ESCOLHEDOR DE PAÍS DO CLÃ (11/09/2026) — `v-model` com o código, ou `null` para "sem país".
 *
 * Usado na fundação (`CreateClanModal`) e na troca pelo líder (`ClanDetails`, com `so-painel`: lá
 * quem abre e fecha é o link do cabeçalho, como o da bandeira).
 *
 * **Cada opção leva o nome, e não só a bandeira.** A 24px Chade e Romênia, Mônaco e Indonésia são a
 * mesma arte; uma grade de bandeiras mudas é um teste de vexilologia. E a lista é ordenada pelo nome
 * NA LÍNGUA DO JOGO, com o `Collator` dela — "Åland" no lugar certo em cada idioma.
 */
export default {
  name: 'EscolhaDePais',
  components: { BandeiraDePais, FishIcon },
  props: {
    modelValue: { type: String, default: null },
    desabilitado: { type: Boolean, default: false },
    /** Só a lista, sempre aberta — quem usa controla quando ela aparece. */
    soPainel: { type: Boolean, default: false },
  },
  emits: ['update:modelValue'],
  data() {
    return { aberto: false, busca: '' };
  },
  computed: {
    idioma() {
      return this.$i18n.locale;
    },
    paises() {
      const traduzir = (chave) => this.$t(chave);
      const ordem = new Intl.Collator(this.idioma);
      return CODIGOS_DE_PAIS.map((codigo) => {
        const nome = nomeDoPais(codigo, this.idioma, traduzir);
        return { codigo, nome, procura: semAcento(nome) };
      }).sort((a, b) => ordem.compare(a.nome, b.nome));
    },
    filtrados() {
      const busca = semAcento(this.busca.trim());
      if (!busca) return this.paises;
      return this.paises.filter((p) => p.procura.includes(busca) || p.codigo === busca);
    },
    nomeAtual() {
      return nomeDoPais(this.modelValue, this.idioma, (chave) => this.$t(chave));
    },
  },
  mounted() {
    if (this.soPainel) this.aoAbrir();
  },
  methods: {
    alternar() {
      this.aberto = !this.aberto;
      if (this.aberto) this.aoAbrir();
    },

    /**
     * Ao abrir: foco na busca, e a grade rolada até o país atual. Sem isso, quem escolheu "Brasil"
     * reabre a lista no Afeganistão e acha que a escolha se perdeu.
     */
    aoAbrir() {
      this.busca = '';
      this.$nextTick(() => {
        this.$refs.busca?.focus({ preventScroll: true });
        const grade = this.$refs.grade;
        const atual = grade?.querySelector('.escolha-de-pais__opcao--atual');
        // `offsetTop` conta a partir da grade porque ela é `position: relative` — ver o CSS.
        if (grade && atual) grade.scrollTop = atual.offsetTop - grade.clientHeight / 3;
      });
    },

    escolher(codigo) {
      this.$emit('update:modelValue', codigo);
      this.aberto = false;
    },
  },
};
</script>

<style scoped>
.escolha-de-pais {
  display: flex;
  flex-direction: column;
  gap: var(--e-1);
}

.escolha-de-pais__campo {
  display: flex;
  align-items: center;
  gap: var(--e-2);
  width: 100%;
  padding: var(--e-2);
  border: 2px solid var(--madeira);
  background: var(--papel-frio);
  color: var(--tinta);
  font-family: var(--fonte-corpo);
  font-size: var(--f-2);
  text-align: left;
  cursor: pointer;
}

.escolha-de-pais__campo:focus-visible {
  outline: 2px solid var(--latao);
  outline-offset: -2px;
}

.escolha-de-pais__trocar {
  font-size: var(--f-1);
  color: var(--latao);
  text-decoration: underline dashed;
}

.escolha-de-pais__painel {
  display: flex;
  flex-direction: column;
  gap: var(--e-2);
  padding: var(--e-2);
  border: 2px solid var(--madeira);
  background: var(--papel-frio);
}

.escolha-de-pais__busca {
  border: 2px solid var(--madeira);
  background: var(--papel);
  padding: var(--e-1) var(--e-2);
  font-family: var(--fonte-corpo);
  font-size: var(--f-2);
  color: var(--tinta);
}

.escolha-de-pais__busca:focus {
  outline: 2px solid var(--latao);
  outline-offset: -2px;
}

/* A grade rola por dentro: 264 países esticariam o modal a metros de altura. */
.escolha-de-pais__grade {
  position: relative;
  list-style: none;
  margin: 0;
  padding: 0 var(--e-1) 0 0;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(104px, 1fr));
  gap: var(--e-1);
  max-height: clamp(180px, 34vh, 300px);
  overflow-y: auto;
}

.escolha-de-pais__opcao {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--e-1);
  padding: var(--e-1);
  border: 2px solid transparent;
  background: none;
  color: var(--tinta);
  font-family: var(--fonte-corpo);
  cursor: pointer;
  transition: transform var(--t-rapido) var(--curva-quique);
}

@media (hover: hover) {
  .escolha-de-pais__opcao:hover {
    transform: translateY(-2px);
    border-color: var(--papel-sombra);
  }
}

.escolha-de-pais__opcao--atual {
  border-color: var(--latao);
  background: var(--papel);
}

.escolha-de-pais__sem {
  width: 48px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--tinta-fraca);
  outline: 1px dashed var(--tinta-fraca);
}

/* Duas linhas no máximo: "Ilhas Menores Distantes dos Estados Unidos" existe. */
.escolha-de-pais__rotulo {
  font-size: var(--f-1);
  line-height: var(--lh-curto);
  text-align: center;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.escolha-de-pais__vazio {
  margin: 0;
  font-size: var(--f-1);
  color: var(--tinta-fraca);
  font-style: italic;
}
</style>
